import { t } from "@lingui/core/macro";
import { Link, useNavigate } from "@tanstack/react-router";
import { resendSignUpCode, resetPassword, signIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";
import { useDispatch } from "react-redux";

import { toastError } from "@/redux/toasts/toastsReducers";
import { type ChallengedUser } from "@/routes/_unauthenticated/sign-in";
import { AsyncButton } from "@/shared/components/ui/async-button";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";

import AuthPage from "./AuthPage";
import PasswordInput from "./PasswordInput";
import { interpretSignInError, interpretSignInResult, passwordContainsWhitespace } from "./signInFlow";

interface SignInProps {
  setChallengedUser: (challengedUser: ChallengedUser | null) => void;
}
export default function SignIn({ setChallengedUser }: SignInProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const startPasswordReset = async (username: string) => {
    await resetPassword({ username });
    setChallengedUser({ challengeName: "RESET_PASSWORD", email: username });
  };

  const handleSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const username = formData.get("username") as string;
    const password = formData.get("password") as string;

    if (passwordContainsWhitespace(password)) {
      setError(t`Password cannot contain spaces. Please try again.`);

      return;
    }

    setError("");
    setLoading(true);
    try {
      const result = await signIn({ username, password });
      const flowResult = interpretSignInResult(result, username);

      switch (flowResult.action) {
        case "signed_in":
          setChallengedUser(null);
          break;
        case "start_password_reset":
          await startPasswordReset(flowResult.username);
          break;
        case "redirect_unconfirmed":
          await resendSignUpCode({ username: flowResult.username });
          navigate({ to: "/email-verification", search: { email: flowResult.username } });
          dispatch(
            toastError({
              message: t`Your account is not verified. Please check your email for the verification code.`,
            }),
          );
          break;
        case "set_challenged_user":
          setChallengedUser(flowResult.user);
          break;
        case "show_error":
          setError(
            flowResult.kind === "message" && flowResult.message
              ? flowResult.message
              : t`We were unable to sign you in to your account. Please try again later or reach out to support if the issue persists.`,
          );
          break;
      }
    } catch (err) {
      const flowResult = interpretSignInError(err as Error & { code?: string; name?: string }, username);

      switch (flowResult.action) {
        case "start_password_reset":
          try {
            await startPasswordReset(flowResult.username);
          } catch (resetErr) {
            setError((resetErr as Error).message);
          }
          break;
        case "redirect_unconfirmed":
          try {
            await resendSignUpCode({ username: flowResult.username });
            navigate({ to: "/email-verification", search: { email: flowResult.username } });
            dispatch(
              toastError({
                message: t`Your account is not verified. Please check your email for the verification code.`,
              }),
            );
          } catch {
            setError(
              t`Your account is not verified and there was an error resending the email verification code. Please try logging in again`,
            );
          }
          break;
        case "show_error":
          setError(
            flowResult.kind === "message" && flowResult.message
              ? flowResult.message
              : t`We were unable to sign you in to your account. Please try again later or reach out to support if the issue persists.`,
          );
          break;
        default:
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage>
      <form className="flex flex-col gap-4" onSubmit={handleSignIn}>
        <Input
          type="email"
          autoComplete="username"
          placeholder={t`Enter your Email`}
          data-cy="login-email"
          name="username"
          required
        />
        <PasswordInput
          autoComplete="current-password"
          placeholder={t`Enter your Password`}
          name="password"
          required
          data-cy="login-password"
        />
        {error && (
          <p className="text-sm text-destructive" data-cy="login-error">
            {error}
          </p>
        )}

        <AsyncButton
          type="submit"
          loading={loading}
          loadingText={t`Signing In`}
          minLoadTime={1500}
          className="w-full"
          data-cy="login-button"
        >
          {t`Sign In`}
        </AsyncButton>

        <div className="space-y-1">
          <div className="flex justify-center">
            <Button render={<Link to="/forgot-password" />} variant="link" type="button" className="font-md px-0">
              {t`Forgot your password?`}
            </Button>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {t`Not yet a client?`}{" "}
            <Link
              className="font-medium text-primary underline-offset-4 hover:underline"
              to="/sign-up"
              search={{ utm_source: "login-screen" }}
            >
              {t`Sign up today`}
            </Link>
          </p>
        </div>
      </form>
    </AuthPage>
  );
}
